import React from 'react';

import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { ConfluenceIcon } from '@atlaskit/logo';
import GlobeIcon from '@atlaskit/icon/core/globe';

import { fg } from '@confluence/feature-gating';
import type { useTeamData } from '@confluence/permissions-principals/entry-points/useTeamData.ts';

type TeamDataType = ReturnType<typeof useTeamData>['teamData'];

import type { SpacePermissionListQuery_spacePermissionsAll_nodes as SpacePermissionListNode } from '../graphql/__types__/SpacePermissionListQuery.ts';
import type { GroupCountsQuery_groupCounts_groupCounts as GroupCountRecord } from '../graphql/__types__/GroupCountsQuery.ts';
import type {
	SpaceRoleAssignmentsByCriteriaQuery_spaceRoleAssignmentsByCriteria_nodes_principal as SpaceRoleAssignmentNodePrincipal,
	SpaceRoleAssignmentsByCriteriaQuery_spaceRoleAssignmentsByCriteria_nodes_array,
} from '../graphql/__types__/SpaceRoleAssignmentsByCriteriaQuery.ts';
import type {
	SpaceRolesByCriteriaQuery_spaceRolesByCriteria_SpaceRoleConnection_nodes_SpaceRole as SpaceRoleNode,
	ConfluenceRoleAssignabilityCode,
	SpaceRoleType,
} from '../graphql/__types__/SpaceRolesByCriteriaQuery.ts';

import type {
	PrincipalRoleAssignment,
	SpacePermission,
	SpaceRole,
	TableAccountTypeFilterType,
} from './space-roles-types.ts';
import {
	ConfluenceGroupUsageType,
	RoleAssignmentPrincipalType,
	PrincipalFilterType,
} from './space-roles-types.ts';

const AccessClassIcon = () => React.createElement(ConfluenceIcon, { appearance: 'brand' });
const getPrincipalIcon = (principal: SpaceRoleAssignmentNodePrincipal) => {
	switch (principal.__typename) {
		case 'SpaceRoleGroupPrincipal':
			return PeopleGroupIcon;
		case 'SpaceRoleAccessClassPrincipal':
			return principal.principalId === 'anonymous-users' ? GlobeIcon : AccessClassIcon;
		case 'ConfluenceSpaceRoleAppPrincipal':
		case 'SpaceRoleGuestPrincipal':
		case 'SpaceRoleUserPrincipal':
		default:
			return null;
	}
};

export const normalizeSpacePermissionAll = (
	data: SpacePermissionListNode[],
	isCompanyHub: boolean = false,
): SpacePermission[] => {
	return (
		data
			.map<SpacePermission>((item) => ({
				key: item.id,
				groupName: item.group.displayName,
				groupPriority: item.group.priority,
				// Use hubDisplayName if in companyhub context, otherwise fall back to displayName
				actionName: (isCompanyHub && item.hubDisplayName) || item.displayName,
				priority: item.priority,
				// Use hubDescription if in companyhub context, otherwise fall back to description
				description: (isCompanyHub && item.hubDescription) || item.description,
				requiredPermissions: item.requiredSpacePermissions
					? [...item.requiredSpacePermissions].map((p) => p ?? null)
					: null,
			}))
			// Filter out all actions without display names before the deprecation of some permissions is completed
			.filter((item) => !!item.actionName)
	);
};

export const normalizeSpaceRoleList = (
	rawData: readonly (SpaceRoleNode | null | undefined)[],
): SpaceRole[] => {
	return rawData
		.filter((d): d is SpaceRoleNode => d != null)
		.map<SpaceRole>((item) => ({
			id: item.roleId,
			name: item.roleDisplayName,
			description: item.roleDescription,
			permissions: item.spacePermissionList.map((permission) => permission.id),
			roleType: item.roleType as SpaceRoleType,
			confluenceRoleAssignabilityCode:
				item.confluenceRoleAssignabilityCode as ConfluenceRoleAssignabilityCode,
		}));
};

export const getPrincipalTypeFromGraphqlTypename = (type: string): RoleAssignmentPrincipalType => {
	switch (type) {
		case 'SpaceRoleGuestPrincipal':
			// Guests are users.
			return RoleAssignmentPrincipalType.USER;
		case 'ConfluenceSpaceRoleAppPrincipal':
			return RoleAssignmentPrincipalType.APP;
		case 'SpaceRoleGroupPrincipal':
			// Teams are groups.
			return RoleAssignmentPrincipalType.GROUP;
		case 'SpaceRoleUserPrincipal':
			return RoleAssignmentPrincipalType.USER;
		case 'SpaceRoleAccessClassPrincipal':
			return RoleAssignmentPrincipalType.ACCESS_CLASS;
		default:
			return RoleAssignmentPrincipalType.GROUP;
	}
};

export const getPrincipalTypeFromUserPickerType = (
	type: string | undefined,
): RoleAssignmentPrincipalType => {
	switch (type) {
		case 'user':
			return RoleAssignmentPrincipalType.USER;
		case 'team':
			return RoleAssignmentPrincipalType.GROUP;
		case 'group':
			return RoleAssignmentPrincipalType.GROUP;
		case 'external_user':
			return RoleAssignmentPrincipalType.USER;
		case 'custom':
			return RoleAssignmentPrincipalType.ACCESS_CLASS;
		default:
			return RoleAssignmentPrincipalType.USER;
	}
};

export const normalizeAssignments = (
	rawData: SpaceRoleAssignmentsByCriteriaQuery_spaceRoleAssignmentsByCriteria_nodes_array | null,
): PrincipalRoleAssignment[] => {
	if (!rawData) return [];

	return rawData.map<PrincipalRoleAssignment>((item) => {
		const { principal, role } = item;
		const isTeam =
			principal.__typename === 'SpaceRoleGroupPrincipal' &&
			principal.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION;

		return {
			assignmentId: principal.principalId, // BE will support the assignmentId in the future
			principal: {
				id: principal.principalId,
				displayName: principal.displayName,
				type: getPrincipalTypeFromGraphqlTypename(principal.__typename),
				email: 'email' in principal ? principal?.email : undefined,
				avatarUrl: 'profilePicture' in principal ? principal.profilePicture?.path : undefined,
				icon:
					isTeam && fg('confluence_frontend_perms_exp_teams_as_principals')
						? null
						: getPrincipalIcon(principal),
				isGuest: principal.__typename === 'SpaceRoleGuestPrincipal',
				isTeam,
				// teamData will be populated by mergeTeamDataWithAssignments after fetching from AGG
			},
			roleId: role?.roleId || null,
			roleLabel: role?.roleDisplayName || null,
			roleDescription: role?.roleDescription || null,
			// Principal can either be assigned granular space permissions, or permissions through a role
			permissions:
				item?.permissions?.map((permission) => permission.id) ||
				role?.spacePermissionList.map((permission) => permission.id) ||
				[],
			availableRoles: [],
			unavailableRoles: [],
			assignablePermissions:
				(item.assignablePermissions?.map((permission) => permission).filter(Boolean) as string[]) ||
				[],
		};
	});
};

export type GroupCountMap = Record<string, number | null>;

export const mergeGroupCountWithAssignments = (
	groupCountRecords: GroupCountRecord[],
	assignments: PrincipalRoleAssignment[],
): PrincipalRoleAssignment[] => {
	const groupCountMap: GroupCountMap = groupCountRecords.reduce((acc, { key, value }) => {
		if (key !== null && key !== undefined) acc[key] = value;
		return acc;
	}, {});

	return assignments.map((assignment) => ({
		...assignment,
		principal:
			assignment.principal.type === RoleAssignmentPrincipalType.GROUP
				? {
						...assignment.principal,
						groupCount: groupCountMap[assignment.principal.id] ?? undefined,
					}
				: assignment.principal,
	}));
};

export const mergeTeamDataWithAssignments = (
	assignments: PrincipalRoleAssignment[],
	teamData: TeamDataType,
): PrincipalRoleAssignment[] => {
	if (!teamData || !teamData.teamsV2 || teamData.teamsV2?.length === 0) return assignments;

	// Pre-compute a map of team IDs to team info for O(1) lookup
	const teamInfoMap = new Map<string, (typeof teamData.teamsV2)[number]>();
	for (const team of teamData.teamsV2) {
		if (team?.id) {
			teamInfoMap.set(team.id, team);
		}
	}

	return assignments.map((row) => {
		const teamInfo = teamInfoMap.get(row.principal.id);
		if (!teamInfo) return row;

		return {
			...row,
			principal: {
				...row.principal,
				icon: null,
				avatarUrl: teamInfo.largeAvatarImageUrl ?? undefined,
				isTeam: true,
				isVerified: teamInfo.type?.verified ?? undefined,
				teamData: teamInfo,
				teamTypeName: teamInfo.type?.name,
			},
		};
	});
};

const UNLICENSED_USERS_ID = 'authenticated-users';

export const filterOutUnlicensedUsersAssignment = (assignments: PrincipalRoleAssignment[]) =>
	assignments.filter((record) => record.principal.id !== UNLICENSED_USERS_ID);

export const displayPrincipalTypesToAPIPrincipalFilterTypes = (
	displayTypes: TableAccountTypeFilterType[],
): PrincipalFilterType[] => {
	return displayTypes
		.filter((type) => type !== 'TEAM')
		.concat(displayTypes.includes('TEAM') ? [PrincipalFilterType.GROUP] : []);
};

// In spaceRoleAssignmentsByCriteria query, we need to specify the usage type we need to exclude
export const getTypesToFilterOut = (filterTypes: TableAccountTypeFilterType[]) => {
	if (filterTypes.length === 0) return [];

	const typesToFilterOut: ConfluenceGroupUsageType[] = [];
	if (!filterTypes.includes(PrincipalFilterType.GROUP)) {
		typesToFilterOut.push(ConfluenceGroupUsageType.USERBASE_GROUP);
	}
	if (!filterTypes.includes('TEAM')) {
		typesToFilterOut.push(ConfluenceGroupUsageType.TEAM_COLLABORATION);
	}
	if (!filterTypes.includes(PrincipalFilterType.USER_CLASS)) {
		typesToFilterOut.push(ConfluenceGroupUsageType.ADMIN_OVERSIGHT);
	}

	return typesToFilterOut;
};
